import {
  getVisibleModelIds,
  getVisiblePointCloudIds,
  getVisibleTrajectoryIds,
  useDataContext,
} from '@mobile-mapping/providers';
import { LoadState } from '@trimble/data-loading';
import { useSearchParams } from '@solidjs/router';
import { createEffect, createMemo, createSignal, on, onMount } from 'solid-js';
import { useConnectProject } from '@trimble/connect-project-providers';

type SearchParams = {
  tenantTrn?: string;
  pointCloudIds?: string;
  trajectoryIds?: string;
  modelIds?: string;
};

interface UseUrlSyncResult {
  /**
   * Whether the URL asks for any data to be loaded. Known synchronously, before
   * any catalog has answered, which is what lets the app decide up front
   * whether it is opening an empty project.
   */
  urlRequestsData(): boolean;
}

export const useUrlSync = (): UseUrlSyncResult => {
  const [searchParams, setSearchParams] = useSearchParams<SearchParams>();

  const { setSelectedLocation, setSelectedProjectId, selectedTenantTrn } =
    useConnectProject();

  const { dataState, dataLoadingService } = useDataContext();

  // Extract tenant info from URL
  const tenantTrnFromUrl = createMemo(() => {
    const tenantTrn = searchParams.tenantTrn;
    if (!tenantTrn) return undefined;

    const parts = tenantTrn.split(':').map((part) => part.trim());
    if (
      // eslint-disable-next-line no-magic-numbers
      parts.length < 5 ||
      parts[0] !== 'trn' ||
      parts[1] !== 'connect' ||
      // eslint-disable-next-line no-magic-numbers
      parts[2] !== 'projects'
    ) {
      return undefined;
    }
    return {
      // eslint-disable-next-line no-magic-numbers
      location: parts[3],
      // eslint-disable-next-line no-magic-numbers
      projectId: parts[4],
    };
  });

  // Extract point cloud IDs from URL
  const pointCloudIdsFromUrl = createMemo(
    () =>
      searchParams.pointCloudIds
        ?.split(',')
        .map((id) => id.trim())
        .filter((id) => id !== '') ?? []
  );

  // Extract trajectory IDs from URL
  const trajectoryIdsFromUrl = createMemo(
    () =>
      searchParams.trajectoryIds
        ?.split(',')
        .map((id) => id.trim())
        .filter((id) => id !== '') ?? []
  );

  // Extract model IDs from URL
  const modelIdsFromUrl = createMemo(
    () =>
      searchParams.modelIds
        ?.split(',')
        .map((id) => id.trim())
        .filter((id) => id !== '') ?? []
  );

  // Get currently visible point cloud IDs
  const visiblePointCloudIds = createMemo(() => {
    const visiblePointCloudIds = getVisiblePointCloudIds(dataState) ?? [];
    if (visiblePointCloudIds.length === 0) return undefined;

    return visiblePointCloudIds.join(',');
  });

  // Get currently visible trajectory IDs
  const visibleTrajectoryIds = createMemo(() => {
    const visibleTrajectoryIds = getVisibleTrajectoryIds(dataState) ?? [];
    if (visibleTrajectoryIds.length === 0) return undefined;

    return visibleTrajectoryIds.join(',');
  });

  // Get currently visible model IDs
  const visibleModelIds = createMemo(() => {
    const visibleModelIds = getVisibleModelIds(dataState) ?? [];
    if (visibleModelIds.length === 0) return undefined;

    return visibleModelIds.join(',');
  });

  onMount(() => {
    const tenantTrn = tenantTrnFromUrl();
    if (!tenantTrn) return;

    setSelectedLocation(tenantTrn.location);
    setSelectedProjectId(tenantTrn.projectId);
  });

  const [loadInitiated, setLoadInitiated] = createSignal<boolean>(false);

  // Track data loading state and then load items from URL
  createEffect(
    on(
      [
        () => dataState['pointCloud']?.state,
        () => dataState['trajectory']?.state,
        () => dataState['tcFileFolder']?.state,
        loadInitiated,
      ],
      ([pcState, trState, tcState, loadInitiated]) => {
        if (
          pcState !== LoadState.LoadComplete ||
          trState !== LoadState.LoadComplete ||
          tcState !== LoadState.LoadComplete ||
          loadInitiated
        )
          return;

        pointCloudIdsFromUrl().forEach((id) => {
          dataLoadingService.load('pointCloud', id);
        });
        trajectoryIdsFromUrl().forEach((id) => {
          dataLoadingService.load('trajectory', id);
        });
        modelIdsFromUrl().forEach((id) => {
          dataLoadingService.load('model', id);
        });

        setLoadInitiated(true);
      }
    )
  );

  // Update query parameters from app state
  createEffect(
    on(
      [
        selectedTenantTrn,
        visiblePointCloudIds,
        visibleTrajectoryIds,
        visibleModelIds,
        loadInitiated,
      ],
      ([
        tenantTrn,
        visiblePointCloudIds,
        visibleTrajectoryIds,
        visibleModelIds,
        loadInitiated,
      ]) => {
        if (!loadInitiated) {
          return;
        }
        setSearchParams({
          tenantTrn: tenantTrn,
          pointCloudIds: visiblePointCloudIds,
          trajectoryIds: visibleTrajectoryIds,
          modelIds: visibleModelIds,
        });
      }
    )
  );

  const urlRequestsData = createMemo(
    () =>
      pointCloudIdsFromUrl().length > 0 ||
      trajectoryIdsFromUrl().length > 0 ||
      modelIdsFromUrl().length > 0
  );

  return { urlRequestsData };
};
